import React, { useState, useRef, useEffect } from 'react';
import './ReservationForm.css';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { verificarDisponibilidadHabitacion } from '../data/reservas';
import ResultadosBusqueda from './ResultadosBusqueda';
import Modal from './Modal';
import { formatearFecha, normalizarFecha } from '../utils/dateUtils';
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import es from 'date-fns/locale/es';
import { Helmet } from 'react-helmet';
import { trackEvent } from '../utils/analyticsUtils';
import { useSearchParams } from 'react-router-dom';

// Registrar el locale español
registerLocale('es', es);
setDefaultLocale('es');

// Función para generar un código de reserva único de 6 dígitos
const generateReservationCode = () => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

const ReservationForm = () => {
  const [searchParams] = useSearchParams();
  const [fechaEntrada, setFechaEntrada] = useState(null);
  const [fechaSalida, setFechaSalida] = useState(null);
  const [calendarEntradaOpen, setCalendarEntradaOpen] = useState(false);
  const [calendarSalidaOpen, setCalendarSalidaOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    numeroPersonas: '1'
  });

  const [resultados, setResultados] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(true);
  const [cargando, setCargando] = useState(false);
  const [reservaExitosa, setReservaExitosa] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    message: ''
  });
  
  // Estado para almacenar las habitaciones obtenidas de Firestore
  const [habitaciones, setHabitaciones] = useState([]);
  const [cargandoHabitaciones, setCargandoHabitaciones] = useState(true);
  const [errorHabitaciones, setErrorHabitaciones] = useState(null);
  
  // Estado para almacenar la habitación preseleccionada desde la URL
  const [habitacionPreseleccionada, setHabitacionPreseleccionada] = useState(null);
  
  const fechaActual = new Date();
  
  // Referencias para los inputs de fecha
  const fechaEntradaRef = useRef(null);
  const fechaSalidaRef = useRef(null);
  
  // Efecto para procesar los parámetros de URL cuando se carga el componente
  useEffect(() => {
    const checkinParam = searchParams.get('checkin');
    const checkoutParam = searchParams.get('checkout');
    const personsParam = searchParams.get('persons');
    const roomParam = searchParams.get('room');
    
    // Procesamos las fechas si están presentes en la URL
    if (checkinParam && checkoutParam) {
      try {
        const checkinDate = new Date(checkinParam);
        const checkoutDate = new Date(checkoutParam);
        
        // Verificar que las fechas sean válidas
        if (!isNaN(checkinDate.getTime()) && !isNaN(checkoutDate.getTime())) {
          setFechaEntrada(checkinDate);
          setFechaSalida(checkoutDate);
        }
      } catch (error) {
        console.error('Error al procesar fechas de URL:', error);
      }
    }
    
    // Procesamos el número de personas
    if (personsParam && !isNaN(parseInt(personsParam))) {
      setFormData(prev => ({
        ...prev,
        numeroPersonas: personsParam
      }));
    }
    
    // Guardamos el ID de la habitación preseleccionada
    if (roomParam) {
      setHabitacionPreseleccionada(roomParam);
    }
    
  }, [searchParams]);
  
  // Efecto para cargar las habitaciones desde Firestore al montar el componente
  useEffect(() => {
    const fetchHabitaciones = async () => {
      setCargandoHabitaciones(true);
      try {
        const habitacionesCollection = collection(db, 'habitaciones');
        const habitacionesSnapshot = await getDocs(habitacionesCollection);
        
        if (habitacionesSnapshot.docs.length > 0) {
          const habitacionesList = habitacionesSnapshot.docs.map(doc => ({
            id: doc.id,
            nombre: doc.data().nombre || 'Sin nombre',
            tipo: doc.data().tipo || 'Estándar',
            capacidad: doc.data().capacity || 1,
            precio: doc.data().precio || 0,
            preciosPorOcupacion: doc.data().preciosPorOcupacion || {},
            descripcion: doc.data().descripcion || '',
            imagen: doc.data().imagenes && doc.data().imagenes.length > 0 ? doc.data().imagenes[0] : '/images/habitacion-default.jpg',
            imagenes: doc.data().imagenes || []
          }));
          
          // Ordenar habitaciones por ID
          habitacionesList.sort((a, b) => parseInt(a.id) - parseInt(b.id));
          
          setHabitaciones(habitacionesList);
          setErrorHabitaciones(null);
        } else {
          setErrorHabitaciones('No se encontraron habitaciones en la base de datos.');
          setHabitaciones([]);
        }
      } catch (err) {
        console.error('Error al cargar habitaciones:', err);
        setErrorHabitaciones('Error al cargar habitaciones: ' + err.message);
        setHabitaciones([]);
      } finally {
        setCargandoHabitaciones(false);
      }
    };
    
    fetchHabitaciones();
  }, []);
  
  // Efecto para realizar búsqueda automática si venimos con parámetros completos
  useEffect(() => {
    // Si tenemos todos los datos necesarios y se han cargado las habitaciones
    if (
      fechaEntrada && 
      fechaSalida && 
      formData.numeroPersonas && 
      habitacionPreseleccionada && 
      !cargandoHabitaciones && 
      habitaciones.length > 0 &&
      !resultados // Evitamos buscar si ya tenemos resultados
    ) {
      // Iniciamos búsqueda automática
      handleSubmit(new Event('submit', { cancelable: true }));
    }
  }, [fechaEntrada, fechaSalida, formData.numeroPersonas, habitacionPreseleccionada, cargandoHabitaciones, habitaciones, resultados]);

  // Función para manejar cambios en la fecha de entrada
  const handleFechaEntradaChange = (fecha) => {
    setFechaEntrada(fecha);
    setCalendarEntradaOpen(false);
    
    // Si se selecciona una fecha de entrada, establecer automáticamente la fecha de salida al día siguiente
    if (fecha) {
      const fechaSalida = new Date(fecha);
      fechaSalida.setDate(fechaSalida.getDate() + 1);
      setFechaSalida(fechaSalida);
    } else {
      setFechaSalida(null);
    }
    
    // Si la fecha de entrada se establece, automáticamente abrimos el selector de fecha de salida
    if (fecha) {
      setTimeout(() => {
        setCalendarSalidaOpen(true);
      }, 200);
    }
  };
  
  // Funciones para abrir el calendario manualmente al hacer clic
  const handleFechaEntradaClick = () => {
    setTimeout(() => {
      if (fechaEntradaRef && fechaEntradaRef.current) {
        fechaEntradaRef.current.setFocus();
      }
    }, 50);
  };
  
  const handleFechaSalidaClick = () => {
    setTimeout(() => {
      if (fechaSalidaRef && fechaSalidaRef.current && fechaEntrada) {
        fechaSalidaRef.current.setFocus();
      }
    }, 50);
  };
  
  // Función para manejar cambios en la fecha de salida
  const handleFechaSalidaChange = (fecha) => {
    // Si se selecciona la misma fecha que ya está establecida, 
    // solo cerramos el calendario sin cambiar el estado
    if (fechaSalida && fecha && fecha.getTime() === fechaSalida.getTime()) {
      setCalendarSalidaOpen(false);
      return;
    }
    
    // Si es una fecha nueva, actualizamos el estado y cerramos el calendario
    setFechaSalida(fecha);
    setCalendarSalidaOpen(false);
  };
  
  // Función para manejar cambios en los selectores
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCargando(true);
    
    // Limpiar los resultados anteriores para forzar una remontaje completo del componente
    setResultados(null);

    try {
      // Verificar que las fechas sean válidas
      if (!fechaEntrada || !fechaSalida) {
        setModalConfig({
          isOpen: true,
          message: 'Por favor, seleccione las fechas de entrada y salida.'
        });
        setCargando(false);
        return;
      }

      // Verificar que haya habitaciones cargadas
      if (habitaciones.length === 0) {
        setModalConfig({
          isOpen: true,
          message: 'No hay habitaciones disponibles en este momento. Por favor, inténtelo más tarde.'
        });
        setCargando(false);
        return;
      }

      // Convertir fechas a formato ISO para comparación
      const fechaEntradaISO = fechaEntrada.toISOString().split('T')[0];
      const fechaSalidaISO = fechaSalida.toISOString().split('T')[0];

      if (fechaEntradaISO >= fechaSalidaISO) {
        setModalConfig({
          isOpen: true,
          message: 'La fecha de salida debe ser posterior a la fecha de entrada.'
        });
        setCargando(false);
        return;
      }

      const personasRequeridas = parseInt(formData.numeroPersonas);

      // Registrar el evento de búsqueda
      trackEvent('search_rooms', {
        check_in_date: fechaEntradaISO,
        check_out_date: fechaSalidaISO,
        guests: personasRequeridas
      });

      // Verificar disponibilidad de cada habitación
      const disponibilidadPromises = habitaciones.map(async (habitacion) => {
        const disponible = await verificarDisponibilidadHabitacion(habitacion.id, fechaEntradaISO, fechaSalidaISO);
        return { habitacion, disponible };
      });

      const resultadosDisponibilidad = await Promise.all(disponibilidadPromises);
      
      // Filtrar solo las habitaciones disponibles
      const habitacionesFiltradas = resultadosDisponibilidad
        .filter(resultado => resultado.disponible)
        .map(resultado => resultado.habitacion);

      if (habitacionesFiltradas.length === 0) {
        setModalConfig({
          isOpen: true,
          message: 'No hay habitaciones disponibles para las fechas seleccionadas.'
        });
        setCargando(false);
        return;
      }
      
      // Si tenemos una habitación preseleccionada, verificamos que esté disponible
      if (habitacionPreseleccionada) {
        const habitacionEspecificaDisponible = habitacionesFiltradas.find(h => h.id === habitacionPreseleccionada);
        
        if (!habitacionEspecificaDisponible) {
          setModalConfig({
            isOpen: true,
            message: 'La habitación seleccionada no está disponible para las fechas seleccionadas. Por favor, seleccione otra habitación o cambie las fechas.'
          });
          setCargando(false);
          return;
        }
      }

      // Establecer resultados con un pequeño retraso para asegurar el remontaje
      setTimeout(() => {
        setResultados(habitacionesFiltradas);
      }, 50);

      // Cuando todo está listo, ocultamos el formulario y mostramos los resultados
      setMostrarFormulario(false);

    } catch (error) {
      console.error('Error al buscar disponibilidad:', error);
      setModalConfig({
        isOpen: true,
        message: 'Ocurrió un error al buscar disponibilidad. Por favor, intente nuevamente.'
      });
    } finally {
      setCargando(false);
    }
  };

  const handleReservaExitosa = (reservaData) => {
    // Registrar el evento de reserva exitosa
    trackEvent('complete_reservation', {
      reservation_id: reservaData.codigoReserva,
      check_in_date: reservaData.fechaEntrada,
      check_out_date: reservaData.fechaSalida,
      room_type: reservaData.tipoHabitacion,
      room_id: reservaData.habitacionId,
      guests: reservaData.numeroPersonas,
      total_amount: reservaData.costoTotal,
      currency: 'CLP'
    });
    
    setResultados(null);
    setMostrarFormulario(true);
    setReservaExitosa(true);
  };

  const handleVolverABuscar = () => {
    // Reset all reservation-related state
    setResultados(null);
    setMostrarFormulario(true);
    
    // Reset the preselected room (important for reservations that started from landing page)
    setHabitacionPreseleccionada(null);
    
    // Reset form data to defaults
    setFormData({
      numeroPersonas: '1'
    });
    
    // Reset the dates to null to force user to select new dates
    setFechaEntrada(null);
    setFechaSalida(null);
    
    // Remove URL parameters by replacing the current URL with the base reservation page URL
    window.history.replaceState({}, '', '/reservar');
    
    console.log('Full search reset performed');
  };

  const cerrarModal = () => {
    setModalConfig({ isOpen: false, message: '' });
  };

  if (reservaExitosa) {
    return (
      <div className="mensaje-exito">
        <h3>¡Reserva realizada con éxito!</h3>
        <p>Gracias por elegir nuestro hospedaje. Te hemos enviado un correo con los detalles de tu reserva y un código único de 6 dígitos para gestionarla.</p>
        <button onClick={handleVolverABuscar} className="btn-volver">
          Volver a buscar
        </button>
      </div>
    );
  }

  if (resultados && !mostrarFormulario) {
    return (
      <ResultadosBusqueda
        habitacionesDisponibles={resultados}
        numeroPersonas={formData.numeroPersonas}
        fechaEntrada={fechaEntrada.toISOString().split('T')[0]}
        fechaSalida={fechaSalida.toISOString().split('T')[0]}
        onReservaExitosa={handleReservaExitosa}
        onVolverABuscar={handleVolverABuscar}
        habitacionPreseleccionada={habitacionPreseleccionada}
      />
    );
  }

  return (
    <main className="reservation-form">
      <Helmet>
        <title>Reservas - Hosteria Futaleufu</title>
        <meta name="description" content="Reserva tu estancia en nuestro hostal. Verifica disponibilidad y realiza tu reserva online de forma segura y sencilla." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <section className="reservation-header" aria-label="Formulario de reserva">
        <h1>Reserva tu Estancia</h1>
        <p>Verifica la disponibilidad y realiza tu reserva de forma sencilla</p>
      </section>

      <section className="reservation-form-container" aria-label="Formulario de reserva">
        <Modal
          isOpen={modalConfig.isOpen}
          onClose={cerrarModal}
          message={modalConfig.message}
        />
        
        <form onSubmit={handleSubmit} className="reservation-form">
          <div className="dates-container">
            <div className="form-group">
              <label htmlFor="fechaEntrada">Fecha de Entrada:</label>
              <DatePicker
                id="fechaEntradaPicker"
                selected={fechaEntrada}
                onChange={handleFechaEntradaChange}
                minDate={fechaActual}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/aaaa"
                className="custom-datepicker"
                calendarClassName="custom-calendar"
                popperClassName="custom-popper"
                wrapperClassName="datepicker-wrapper"
                showPopperArrow={true}
                monthsShown={1}
                required
                autoComplete="off"
                onChangeRaw={(e) => e.preventDefault()}
                readOnly={true}
                open={calendarEntradaOpen}
                onInputClick={() => setCalendarEntradaOpen(true)}
                onClickOutside={() => setCalendarEntradaOpen(false)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="fechaSalida">Fecha de Salida:</label>
              <DatePicker
                id="fechaSalidaPicker"
                selected={fechaSalida}
                onChange={handleFechaSalidaChange}
                minDate={fechaEntrada ? new Date(fechaEntrada.getTime() + 86400000) : null} // 86400000 ms = 1 día
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/aaaa"
                className="custom-datepicker"
                calendarClassName="custom-calendar"
                popperClassName="custom-popper"
                wrapperClassName="datepicker-wrapper"
                showPopperArrow={true}
                monthsShown={1}
                required
                disabled={!fechaEntrada}
                autoComplete="off"
                onChangeRaw={(e) => e.preventDefault()}
                readOnly={true}
                open={calendarSalidaOpen}
                onInputClick={() => fechaEntrada && setCalendarSalidaOpen(true)}
                onClickOutside={() => setCalendarSalidaOpen(false)}
                onSelect={(fecha) => {
                  // Aseguramos que el calendario se cierre incluso si se selecciona la misma fecha
                  handleFechaSalidaChange(fecha);
                  setCalendarSalidaOpen(false);
                }}
              />
            </div>
          </div>

          <div className="guests-rooms-container">
            <div className="form-group">
              <label htmlFor="numeroPersonas">Número de Personas:</label>
              <div className="contador-personas">
                <button 
                  className="btn-contador"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setFormData(prev => ({ ...prev, numeroPersonas: Math.max(1, parseInt(prev.numeroPersonas) - 1) }))
                  }}
                  disabled={formData.numeroPersonas === '1'}
                  type="button"
                >
                  -
                </button>
                <input
                  type="number"
                  id="numeroPersonas"
                  name="numeroPersonas"
                  value={formData.numeroPersonas}
                  min="1"
                  max="16"
                  readOnly
                  className="input-contador"
                />
                <button 
                  className="btn-contador"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setFormData(prev => ({ ...prev, numeroPersonas: Math.min(16, parseInt(prev.numeroPersonas) + 1) }))
                  }}
                  disabled={formData.numeroPersonas === '16'}
                  type="button"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <button type="submit" className="submit-button" disabled={cargando}>
            {cargando ? 'Buscando disponibilidad...' : 'Consultar Disponibilidad'}
          </button>
        </form>
      </section>

      {/* WhatsApp Button */}
      <a 
        href="https://api.whatsapp.com/send/?phone=56950620081&text&type=phone_number&app_absent=0" 
        className="whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contactar por WhatsApp"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" className="whatsapp-icon">
          <path fill="currentColor" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </main>
  );
};

export default ReservationForm; 